<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import config from "@/config";
import logoUrl from "@/assets/img/auth/bookmenow-logo.svg";
import mapUrl from "@/assets/img/auth/ukraine-map.svg";

const props = defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: Number,
    default: null,
  },
});

const LAYOUTS = {
  wide: {
    width: 1008,
    height: 800,
    step: 88,
    maxScale: 1,
    tiles: [
      [2, 0],
      [1, 1],
      [2, 1],
      [3, 1],
      [0, 2],
      [1, 2],
      [2, 2],
      [4, 2],
      [0, 3],
      [1, 3],
      [2, 3],
      [3, 3],
      [4, 3],
      [0, 4],
      [1, 4],
      [3, 4],
      [4, 4],
    ],
    moreTile: [3, 2],
  },
  compact: {
    width: 375,
    height: 395,
    minHeight: 116,
    step: 60,
    maxScale: 1.2,
    tiles: [
      [1, 0],
      [0, 1],
      [1, 1],
      [2, 1],
      [0, 2],
      [2, 2],
    ],
    moreTile: [1, 2],
  },
};

const CATEGORIES = [
  { label: "Готелі", compact: [198, 129] },
  { label: "Будиночки", compact: [52, 164] },
  { label: "Глемпінги", compact: [281, 306] },
  { label: "Бази відпочинку", compact: [39, 318] },
  { label: "Спа-комплекси", compact: [248, 179] },
];

const CITIES = [
  [46.37, 25.78],
  [10.46, 31.79],
  [47.48, 73.79],
  [77.93, 29.86],
  [71.38, 48.99],
  [71.9, 56.82],
  [35.01, 39.38],
  [50.61, 11.04],
  [21, 51.15],
  [14.23, 43.26],
  [57.95, 71.87],
  [26.81, 37],
  [55.99, 48.45],
  [94.95, 47.62],
  [68.65, 34.93],
  [22.75, 22.03],
  [70.01, 18.43],
  [19.11, 35.37],
  [0.83, 47.04],
  [54.87, 36.73],
  [36.06, 26.6],
  [17.62, 20.44],
  [86.63, 54.6],
  [66.16, 92.93],
];

const root = ref(null);
const scale = ref(1);
const logos = ref([]);
const isHeightAnimated = ref(false);

let resizeObserver = null;
let animationTimer = null;
let canAnimateHeight = false;

const layout = computed(() => (props.compact ? LAYOUTS.compact : LAYOUTS.wide));

const rootStyle = computed(() => {
  if (!props.compact) {
    return null;
  }

  const fullHeight = Math.round(layout.value.height * scale.value);
  const minHeight = Math.round(layout.value.minHeight * scale.value);
  const height =
    props.maxHeight === null
      ? fullHeight
      : Math.max(minHeight, Math.min(fullHeight, props.maxHeight));

  return { height: `${height}px` };
});

const stageStyle = computed(() => ({
  width: `${layout.value.width}px`,
  height: `${layout.value.height}px`,
  "--showcase-scale": scale.value,
}));

const tiles = computed(() => {
  if (!logos.value.length) {
    return [];
  }

  return layout.value.tiles.map(([column, row], index) => ({
    key: `${column}-${row}`,
    style: getTileStyle(column, row),
    url: logos.value[index % logos.value.length],
  }));
});

const moreTileStyle = computed(() => getTileStyle(...layout.value.moreTile));

function getTileStyle(column, row) {
  return {
    left: `${column * layout.value.step}px`,
    top: `${row * layout.value.step}px`,
  };
}

function getCategoryStyle(category) {
  if (!props.compact) {
    return null;
  }

  const [left, top] = category.compact;

  return { left: `${left}px`, top: `${top}px` };
}

function updateScale() {
  if (!root.value) {
    return;
  }

  const { width, height } = root.value.getBoundingClientRect();
  const { width: stageWidth, height: stageHeight, maxScale } = layout.value;
  const widthScale = width / stageWidth;
  const fitScale = props.compact
    ? widthScale
    : Math.min(widthScale, height / stageHeight);

  scale.value = Math.max(0, Math.min(maxScale, fitScale));
}

async function loadLogos() {
  try {
    const response = await fetch(
      `${config.API_URL_BASE}/authentication/tenantLogos`,
    );

    if (!response.ok) {
      return;
    }

    const result = await response.json();

    logos.value = Array.isArray(result.logos)
      ? result.logos.filter((url) => typeof url === "string" && url)
      : [];
  } catch (error) {
    logos.value = [];
  }
}

function hideBrokenLogo(event) {
  event.target.style.visibility = "hidden";
}

watch(
  () => props.compact,
  async () => {
    await nextTick();
    updateScale();
  },
);

watch(
  () => props.maxHeight,
  () => {
    isHeightAnimated.value = canAnimateHeight;
  },
);

onMounted(() => {
  resizeObserver = new ResizeObserver(updateScale);
  resizeObserver.observe(root.value);
  updateScale();
  loadLogos();
  animationTimer = setTimeout(() => {
    canAnimateHeight = true;
  });
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  clearTimeout(animationTimer);
});
</script>

<template>
  <div
    ref="root"
    class="auth-showcase"
    :class="{
      'auth-showcase--compact': compact,
      'auth-showcase--animated': isHeightAnimated,
    }"
    :style="rootStyle"
  >
    <div class="auth-showcase__stage" :style="stageStyle">
      <div class="auth-showcase__map" aria-hidden="true">
        <img class="auth-showcase__map-image" :src="mapUrl" alt="" />
        <span
          v-for="([left, top], index) in CITIES"
          :key="index"
          class="auth-showcase__city"
          :style="{ left: `${left}%`, top: `${top}%` }"
        ></span>
      </div>

      <img class="auth-showcase__logo" :src="logoUrl" alt="bookmenow" />
      <p class="auth-showcase__subtitle">Хмарна система керування Готелем</p>

      <ul class="auth-showcase__categories">
        <li
          v-for="category in CATEGORIES"
          :key="category.label"
          class="auth-showcase__category"
          :style="getCategoryStyle(category)"
        >
          {{ category.label }}
        </li>
      </ul>

      <Transition name="auth-showcase-fade">
        <div
          v-if="tiles.length"
          class="auth-showcase__tiles"
          aria-hidden="true"
        >
          <span
            v-for="tile in tiles"
            :key="tile.key"
            class="auth-showcase__tile"
            :style="tile.style"
          >
            <img
              class="auth-showcase__tile-logo"
              :src="tile.url"
              alt=""
              decoding="async"
              @error="hideBrokenLogo"
            />
          </span>
          <span
            class="auth-showcase__tile auth-showcase__tile--more"
            :style="moreTileStyle"
          >
            і багато<br />інших
          </span>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.auth-showcase {
  position: relative;
  overflow: hidden;
  font-family: var(--font-manrope);
  color: #fff;

  &__stage {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(var(--showcase-scale));
  }

  &__map {
    position: absolute;
    top: 66px;
    left: -37px;
    width: 1079px;
    height: 718px;
  }

  &__map-image {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__city {
    position: absolute;
    width: 2.2px;
    height: 2.2px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 0 4.5px rgba(255, 255, 255, 0.4);
    transform: translate(-50%, -50%);
  }

  &__logo {
    position: absolute;
    top: 56px;
    left: 56px;
    width: 405px;
    height: 76px;
  }

  &__subtitle {
    position: absolute;
    top: 144px;
    left: 56px;
    margin: 0;
    font-size: 24px;
    font-weight: 400;
    line-height: 24px;
    white-space: nowrap;
    color: #fff;
  }

  &__categories {
    position: absolute;
    top: 197px;
    left: 56px;
    display: flex;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__category {
    padding: 2px 12px;
    border: 1px solid rgba(148, 193, 169, 0.8);
    border-radius: 20px;
    background: rgba(148, 193, 169, 0.2);
    backdrop-filter: blur(2px);
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
    white-space: nowrap;
  }

  &__tiles {
    position: absolute;
    top: 288px;
    left: 286px;
  }

  &__tile {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    overflow: hidden;
    border-radius: 8px;
    background: #fff;
  }

  &__tile-logo {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__tile--more {
    align-items: flex-end;
    justify-content: flex-start;
    padding: 8px;
    border: 1px solid var(--forest-50);
    background: rgba(32, 67, 51, 0.05);
    backdrop-filter: blur(1px);
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    color: var(--forest-50);
  }

  &--compact {
    width: 100%;

    &.auth-showcase--animated {
      transition: height 0.3s ease;
    }

    .auth-showcase__stage {
      top: 0;
      transform: translateX(-50%) scale(var(--showcase-scale));
      transform-origin: top center;
    }

    .auth-showcase__map {
      top: 114px;
      left: 0;
      width: 375px;
      height: 250px;
    }

    .auth-showcase__map-image {
      opacity: 0.6;
    }

    .auth-showcase__city {
      width: 1.9px;
      height: 1.9px;
      box-shadow: 0 0 3px rgba(255, 255, 255, 0.4);
    }

    .auth-showcase__logo {
      top: 32px;
      left: 97px;
      width: 182px;
      height: 34px;
    }

    .auth-showcase__subtitle {
      top: 64px;
      left: 0;
      width: 100%;
      font-size: 16px;
      text-align: center;
    }

    .auth-showcase__categories {
      inset: 0;
      display: block;
    }

    .auth-showcase__category {
      position: absolute;
      padding: 2px 8px;
      font-size: 10px;
      line-height: 16px;
      z-index: 2;
    }

    .auth-showcase__tiles {
      top: 146px;
      left: 97px;
    }

    .auth-showcase__tile {
      width: 56px;
      height: 56px;
    }

    .auth-showcase__tile--more {
      padding: 6px;
      font-size: 10px;
      line-height: 10px;
    }
  }
}

.auth-showcase-fade-enter-active {
  transition: opacity 0.3s ease;
}

.auth-showcase-fade-enter-from {
  opacity: 0;
}
</style>
